import React from "react";

import CheckIcon from "@uui/Check";
import CopyIcon from "@uui/Copy01";

import Button, { ButtonProps } from "@mui/material/Button";
import Popover, { PopoverProps } from "@mui/material/Popover";
import styled from "@mui/material/styles/styled";
import Typography, { TypographyProps } from "@mui/material/Typography";

import CopyButton from "@rui/CopyButton";
import IconButton, { IconButtonProps } from "@rui/IconButton";
import { Column, Row, StackProps } from "@rui/Stack";

export const ListItem = styled(Row)(({ theme }) => ({
  columnGap: theme.spacing(1),
  justifyContent: "space-between",
  "& .list-item__icon": {
    fontSize: "18px",
    color: theme.palette.shades.neutral[200],
  },
}));

export const CitationButton = styled<React.FC<ButtonProps>>((props) => (
  <Button variant="outlined" {...props} />
))(({ theme }) => ({
  ...theme.typography.disclaimer,
  backgroundColor: "#FFF",
  borderColor: theme.palette.shades.neutral[100],
  padding: theme.spacing(0.25, 0.5),
  minWidth: 0,
  height: "fit-content",
  color: theme.palette.shades.neutral[500],
  fontFamily: theme.typography.fontFamilySecondary,
  flexShrink: 0,
  "& .MuiButton-startIcon": {
    margin: theme.spacing(0, 0.5, 0, 0),
  },
  "& .citation__divider": {
    color: theme.palette.shades.neutral[150],
    fontSize: "10px",
  },
  "&:hover": {
    backgroundColor: "#FFF",
  },
}));

export const CitationPopover = styled<React.FC<PopoverProps>>((props) => (
  <Popover
    {...props}
    anchorOrigin={{ vertical: "top", horizontal: "right" }}
    transformOrigin={{ vertical: 0, horizontal: -8 }}
  />
))(({ theme }) => ({
  pointerEvents: "none",
  "& .MuiPaper-root": {
    border: "1px solid",
    borderColor: theme.palette.shades.primary[500],
    borderRadius: "2px 16px 16px 16px",
    padding: theme.spacing(1),
    maxWidth: "220px",
  },
}));

export const CitationPopoverText = styled<React.FC<TypographyProps>>(
  (props) => <Typography variant="disclaimer" {...props} />
)(({ theme }) => ({
  color: theme.palette.shades.neutral[700],
  fontFamily: theme.typography.fontFamilySecondary,
  wordWrap: "break-word",
}));

export const FloatingCopyButton = styled<React.FC<IconButtonProps>>((props) => (
  <IconButton variant="outlined" {...props} />
))(({ theme }) => ({
  position: "absolute",
  flexShrink: 0,
  top: 0,
  left: 0,
  borderRadius: "6px",
  backgroundColor: "#FFF",
  padding: "4px",
  "& .MuiSvgIcon-root": {
    fontSize: "16px",
    color: theme.palette.shades.neutral[500],
    "&.checked": {
      color: theme.palette.shades.meadow[600],
    },
  },
}));

const CopyEntity = ({ content }: { content: string }) => (
  <CopyButton
    copyValue={content}
    renderButton={({ onClick }, copied) => (
      <FloatingCopyButton onClick={onClick} className="takeaway__copy">
        {copied ? <CheckIcon className="checked" /> : <CopyIcon />}
      </FloatingCopyButton>
    )}
    stopPropagation
  />
);

const TakeawayWrapper = styled(Row)({
  // space for the copy button
  marginLeft: "-28px",
  paddingLeft: "28px",
  "& .takeaway__copy": {
    display: "none",
  },
  position: "relative",
  "&:hover .takeaway__copy": {
    display: "flex",
  },
});

type TakeawayProps = StackProps & { copyContent?: string };

export const Takeaway = styled<React.FC<TakeawayProps>>(
  ({ copyContent, ...props }) => (
    <TakeawayWrapper>
      <Column {...props} />
      {copyContent && <CopyEntity content={copyContent} />}
    </TakeawayWrapper>
  )
)(({ theme, onClick }) => ({
  borderRadius: "8px",
  padding: theme.spacing(0.5),
  width: "100%",

  ...(onClick && {
    "&:hover": {
      cursor: "pointer",
      backgroundColor: theme.palette.shades.ghost[500],
    },
  }),
}));
